<script setup lang="ts">
import { useCasedI18n } from '@/composables/translations/useCasedI18n'
import type { CustomerIntent } from 'shared/src'
const { t, tc } = useCasedI18n()

const panelOpen = ref(false)
const isEditMode = ref(false)
const hasSubmitted = ref(false)

const intentName = ref('')
const intentDescription = ref('')
const trainingPhrases = ref<Record<string, string>[]>([])
const outputField = ref('')

const emit = defineEmits<{
  save: [intent: CustomerIntent]
}>()

const props = defineProps<{ isNoMatch?: boolean }>()

const errors = reactive({
  intent: '',
  description: '',
  output: '',
})

const resetForm = () => {
  intentName.value = ''
  intentDescription.value = ''
  trainingPhrases.value = []
  outputField.value = ''
  clearErrors()
  hasSubmitted.value = false
}

const clearErrors = () => {
  errors.intent = ''
  errors.description = ''
  errors.output = ''
}

const openPanel = (intent?: CustomerIntent, duplicate?: boolean) => {
  if (intent?.name) {
    isEditMode.value = !duplicate
    intentName.value = intent.name
    intentDescription.value = intent.description
    trainingPhrases.value = (intent.training_phrases ?? []).map((p) => ({ phrase: p }))
    outputField.value = intent.output
  } else {
    isEditMode.value = false
    resetForm()
  }

  panelOpen.value = true
}

const closePanel = () => {
  panelOpen.value = false
  resetForm()
  return true
}

const handleSaveOrUpdate = () => {
  hasSubmitted.value = true
  clearErrors()

  if (!intentName.value.trim()) errors.intent = t('errors.intent')
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!intentDescription.value?.trim()) errors.description = t('errors.description')
  if (!outputField.value.trim()) errors.output = t('errors.output')

  const hasErrors = Object.values(errors).some((err) => err !== '')
  if (hasErrors) return false

  const payload: CustomerIntent = {
    name: intentName.value,
    description: intentDescription.value,
    training_phrases: trainingPhrases.value
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      .map((p) => p.phrase?.trim())
      .filter(Boolean),
    output: outputField.value,
    created_at: new Date().toISOString(),
  }

  emit('save', payload)
  return closePanel()
}

watch(intentName, (val) => {
  if (!hasSubmitted.value) return
  errors.intent = val.trim() ? '' : t('errors.intent')
})
watch(intentDescription, (val) => {
  if (!hasSubmitted.value) return
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  errors.description = val?.trim() ? '' : t('errors.description')
})
watch(outputField, (val) => {
  if (!hasSubmitted.value) return
  errors.output = val.trim() ? '' : t('errors.output')
})

defineExpose({ openPanel })
</script>

<template>
  <ORightPanel
    teleport
    v-model:open="panelOpen"
    :title="isEditMode ? tc('edit_intent') : tc('create_intent')"
  >
    <template #default>
      <div class="flex flex-col space-y-4">
        <OInput
          v-model="intentName"
          required
          :label="t('intent_name')"
          :error-message="errors.intent"
          :tooltip-content="t('intent_name.subtitle')"
          :placeholder="t('intent_name.placeholder')"
          dataTestid="intent-name"
        />
        <VariableMentionTextArea
          v-model="intentDescription"
          required
          :label="t('intent_description')"
          :error-message="errors.description"
          :tooltip-content="t('intent_description.subtitle')"
          :placeholder="t('intent_description.placeholder')"
          dataTestid="intent-description"
          :disabled="props.isNoMatch"
          :readonly="props.isNoMatch"
        />
        <VariableMentionMultiTextArea
          v-if="!props.isNoMatch"
          v-model="trainingPhrases"
          :fields="['phrase']"
          :placeholders="[t('training_phrases.placeholder')]"
          :label="t('training_phrases')"
          :tooltip-content="t('training_phrases.subtitle')"
          dataTestid="training_phrase"
        />
        <OInput
          v-model="outputField"
          required
          :label="t('output')"
          :error-message="errors.output"
          :tooltip-content="t('output.subtitle')"
          :placeholder="t('output.placeholder')"
          dataTestid="intent-output"
        />
      </div>
    </template>

    <template #footer>
      <OButton
        :label="tc('cancel')"
        variant="secondary"
        color="neutral"
        size="L"
        dataTestid="cancel-button"
        @mousedown.prevent
        @click="closePanel"
      />
      <OButton
        :label="isEditMode ? tc('save_changes') : tc('save')"
        size="L"
        @click="handleSaveOrUpdate"
        backgroundColor="success"
      />
    </template>
  </ORightPanel>
  <slot :handle-click="openPanel" />
</template>
